import React, { useState, useEffect, useCallback } from "react";
import { Card, Table, Tabs, Pagination, message, Button, Input, Modal, Upload, Icon } from "antd";
import columns from "./tableConfigs";
import PopupService from "../../services/popup";
import ModalForm from "./modalForm";
import moment from "moment";

const Popups = () => {
  const TabPane = Tabs.TabPane;
  const { Search } = Input;

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [sortedInfo, setSortedInfo] = useState(null);
  const [filteredInfo, setFilteredInfo] = useState(null);
  const [search_text, setSearch_text] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);

  const [formRef, setFormRef] = useState(null);
  const [visible, setVisible] = useState(false);
  const [selectedPopupId, setSelectedPopupId] = useState("");
  const [selectedData, setSelectedData] = useState({});

  const [imagePreviewModalVisible, setImagePreviewModalVisible] = useState(false);
  const [imagePreviewModalVisibleFromTable, setImagePreviewModalVisibleFromTable] = useState(false);
  const [previewImageFromTable, setPreviewImageFromTable] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const [fileList, setFileList] = useState([]);
  const [popupImage, setPopupImage] = useState(null);
  const [uploadedImageKey, setUploadedImageKey] = useState(null);

  // Fetch popups
  const fetchPopups = async () => {
    setLoading(true);
    try {
      const res = await PopupService.getPopups({ pageIndex, pageSize, searchTerm: search_text });
      const formattedData = res.data.map((item, idx) => ({
        key: item._id,
        _id: item._id,
        id: idx + 1 + pageIndex * pageSize,
        url: item.url,
        date: moment(item.updatedAt).format("YYYY-MM-DD"),
        status: item.isActive ? "Active" : "Inactive",
        image: `https://www.onlineworksheetsforkids.com/api/util?key=${item.image}`,
        isActive: item.isActive,
        imageKey: item.image,
      }));
      setData(formattedData);
      setTotalCount(res.total);
    } catch (err) {
      message.error("Failed to load popups");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPopups();
  }, [pageIndex, search_text]);

  // Handle modal
  const handleAddPopupModal = () => {
    setSelectedPopupId("");
    setSelectedData({});
    setFileList([]);
    setPopupImage(null);
    setUploadedImageKey(null);
    setVisible(true);
  };

  const handleEditPopup = (record) => {
    setSelectedPopupId(record._id);
    setSelectedData({
      _id: record._id,
      url: record.url,
      isActive: record.isActive,
      image: record.imageKey,
    });
    
    // Show existing image in fileList for preview
    if (record.imageKey) {
      setFileList([{
        uid: '-1',
        name: 'current-image',
        status: 'done',
        url: `https://www.onlineworksheetsforkids.com/api/util?key=${record.imageKey}`,
        thumbUrl: `https://www.onlineworksheetsforkids.com/api/util?key=${record.imageKey}`,
      }]);
    } else {
      setFileList([]);
    }
    
    setPopupImage(null);
    setUploadedImageKey(null);
    setVisible(true);
  };

  const handleCancel = () => {
    setVisible(false);
    setSelectedPopupId("");
    setSelectedData({});
    setFileList([]);
    setPopupImage(null);
    setUploadedImageKey(null);
    if (formRef) {
      formRef.resetFields();
    }
  };

  // Image preview handlers
  const handleImagePreviewModal = (file) => {
    setPreviewImage(file.url || file.thumbUrl);
    setImagePreviewModalVisible(true);
  };
  const handleCancelModal = () => setImagePreviewModalVisible(false);
  const handleCancelModalFromTable = () => setImagePreviewModalVisibleFromTable(false);
  const fromChild = (image) => {
    setPreviewImageFromTable(image);
    setImagePreviewModalVisibleFromTable(true);
  };

  // Upload handler - upload immediately when image is selected
  const customRequest = async ({ file, onSuccess, onError  }) => {
    const isValidType =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp";

    if (!isValidType) {
      message.error("Only JPEG, PNG, and WEBP image formats are allowed!");
      onError(new Error("Invalid file type"));
      setFileList([]); // 🧹 Clear file preview
      return;
    }
    const isValidSize = file.size / 1024 / 1024 <= 1;
    if (!isValidSize) {
      message.error("Image must be smaller than 1MB!");
    onError(new Error("File too large"));
    setFileList([]); 
      return;
    }

    const img = new Image();
    img.onload = async () => {
      if (img.width !== 552 || img.height !== 444) {
        message.error("Image dimensions must be exactly 552x444!");
        onError(new Error("File too large"));
        setFileList([]); // 🧹 Clear file preview
        return;
      }
      
      // Upload image immediately
      try {
        setLoading(true);
        const uploadRes = await PopupService.uploadToS3({ file });
        
        setUploadedImageKey(uploadRes?.fileName);
        setPopupImage(file);
        onSuccess("ok");
        setLoading(false);
      } catch (err) {
        message.error("Failed to upload image.");
         setFileList([]);
        setLoading(false);
        return;
      }
    };
    img.src = URL.createObjectURL(file);
  };

  const handleImageChange = ({ fileList }) => {
    
    setFileList(fileList);
  };

  const handleRemove = () => {
    setPopupImage(null);
    setUploadedImageKey(null);
    setFileList([]);
    // If editing and removing existing image, clear it from selectedData
    if (selectedPopupId && selectedData.image) {
      setSelectedData({ ...selectedData, image: null });
    }
  };

  // Save popup (add or update)
  const handleSubmit = () => {
    setLoading(true);

    formRef.validateFields(async (err, values) => {
      if (err) {
        setLoading(false);
        return;
      }

      try {
        if (!selectedPopupId) {
          // Add new popup
          if (!uploadedImageKey) {
            message.error("Please upload an image.");
            setLoading(false);
            return;
          }

          // Use already uploaded image key
          const imageKey = uploadedImageKey;
          
          const response = await PopupService.addPopup({
            url: values.url,
            image: imageKey,
            isActive: values.isActive,
            width: 552,
            height: 444,
            size: popupImage ? popupImage.size : 0,
          });

          if (response && response._id) {
            message.success("Popup added successfully!");
            formRef.resetFields();
            setLoading(false);
            setVisible(false);
            setFileList([]);
            setPopupImage(null);
            setUploadedImageKey(null);
            fetchPopups();
          } else {
            message.error("Failed to save popup.");
            setLoading(false);
          }
        } else {
          // Update existing popup
          let imageKey = selectedData.image;
          
          // If new image was uploaded, use that key
          if (uploadedImageKey) {
            imageKey = uploadedImageKey;
          }
          
          // If no image exists (was removed), require new upload
          if (!imageKey && !uploadedImageKey) {
            message.error("Please upload an image.");
            setLoading(false);
            return;
          }

          const response = await PopupService.updatePopup({
            popupId: selectedPopupId,
            url: values.url,
            image: imageKey,
            isActive: values.isActive,
          });

          if (response && response._id) {
            message.success("Popup updated successfully!");
            formRef.resetFields();
            setLoading(false);
            setVisible(false);
            setSelectedPopupId("");
            setSelectedData({});
            setFileList([]);
            setPopupImage(null);
            setUploadedImageKey(null);
            fetchPopups();
          } else {
            message.error("Failed to update popup.");
            setLoading(false);
          }
        }
      } catch (err) {
        
        message.error("Error saving popup." ,err);
        console.error(err);
        setLoading(false);
      }
    });
  };

  const handleDeletePopup = async (id) => {
    try {
      await PopupService.deletePopup(id);
      message.success("Popup deleted successfully");
      fetchPopups();
    } catch (err) {
      message.error("Failed to delete popup");
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  // Search handlers
  const onSearch = () => onSearchPress();

  const onSearchChange = (e) => setSearch_text(e.target.value);

  const onSearchPress = async () => {
    setPageIndex(0);
    setCurrentPage(1);
    setLoading(true);

    fetchPopups();
  };

  const onClear = () => {
    setSearch_text(undefined);
    setPageIndex(0);
    setCurrentPage(1);
    fetchPopups();
  };

  const onPageChange = (page) => {
    setPageIndex(page - 1);
    setCurrentPage(page);
  };

  const saveFormRef = useCallback((node) => {
    if (node !== null) {
      setFormRef(node);
    }
  }, []);

  let sorted = sortedInfo || {};
  let filtered = filteredInfo || {};

  return (
    <>
      <ModalForm
        ref={saveFormRef}
        visible={visible}
        loading={loading}
        data={selectedData}
        fileList={fileList}
        setFileList={setFileList}
        handleImageChange={handleImageChange}
        customRequest={customRequest}
        handleRemove={handleRemove}
        handleImagePreviewModal={handleImagePreviewModal}
        onCancel={handleCancel}
        onCreate={handleSubmit}
      />
      <div className="menu-editor-box card-pad-new menu-cate-list users-main-page">
        {/* Image preview from table */}
        <Modal
          title="Popup Preview"
          visible={imagePreviewModalVisibleFromTable}
          footer={null}
          onCancel={handleCancelModalFromTable}
        >
          <img alt="Popup" style={{ width: "100%" }} src={previewImageFromTable} />
        </Modal>

        {/* Image preview from upload */}
        <Modal
          visible={imagePreviewModalVisible}
          footer={null}
          onCancel={handleCancelModal}
        >
          <img alt="Popup" style={{ width: "100%" }} src={previewImage} />
        </Modal>

        <div className="payout-report-box">
          <Card className="gx-card breadcrumb-box" title="">
            <div className="ant-row hd-title">
              <div className="ant-col-md-12">
                <h1>Popups</h1>
              </div>
              <div className="ant-col-md-12 rite-btn">
                <Button
                  type="primary"
                  className="color-white"
                  onClick={handleAddPopupModal}
                >
                  <i className="icon icon-add"></i> Add
                </Button>
              </div>
            </div>

            <div className="ant-row gx-mt-4">
              <div className="ant-col-md-12" style={{ paddingTop: 15 }}>
              </div>
              <div className="ant-col-md-12 rite-btn ">
                <div className="search-box">
                  <Search
                    placeholder="search"
                    style={{ width: 200 }}
                    onSearch={onSearch}
                    onChange={onSearchChange}
                    value={search_text}
                  />
                </div>
                <Button className="my-btn-cust" onClick={onClear}>
                  Clear
                </Button>
              </div>
            </div>

            <Tabs type="card" className="tabs-main-box gx-mt-4">
              <TabPane tab="Default" key="1">
                <Table
                  rowKey={(record) => record.key}
                  pagination={{ pageSize: 100 }}
                  className="gx-table-responsive menu-cate-lisi-table sellads-table"
                  columns={columns({ sorted, filtered, fromChild, onDelete: handleDeletePopup, onEdit: handleEditPopup })}
                  loading={loading}
                  dataSource={data}
                  onChange={handleChange}
                />
                <div className="pageing-box gx-mt-4">
                  <Pagination
                    simple
                    pageSize={pageSize}
                    total={totalCount}
                    onChange={onPageChange}
                    current={currentPage}
                  />
                </div>
              </TabPane>
            </Tabs>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Popups;
